/**
 * VoiceInput — bottom-of-chat mic button + status text.
 * Used when voice mode is active. Drives the useVoiceConversation hook.
 *
 * Props:
 *   voice — the result of useVoiceConversation()
 *   loading — true when the assistant is generating (parent's chat loading state)
 */

import { Mic, MicOff, Loader2, Square } from 'lucide-react';

export default function VoiceInput({ voice, loading }) {
  const { isRecording, transcribing, speaking, durationMs, startRecording, stopRecordingAndSend, stopSpeaking } = voice;

  let statusText;
  if (isRecording) statusText = `● Listening · ${(durationMs / 1000).toFixed(1)}s — tap to send`;
  else if (transcribing) statusText = 'Transcribing…';
  else if (loading) statusText = 'Navigator is thinking…';
  else if (speaking) statusText = 'Navigator is speaking…';
  else statusText = 'Tap to speak';

  return (
    <div className="flex flex-col items-center gap-2 max-w-3xl mx-auto py-2">
      {speaking && (
        <button
          onClick={stopSpeaking}
          className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-200 transition-colors"
        >
          <Square size={12} /> Stop Navigator
        </button>
      )}
      {!isRecording ? (
        <button
          onClick={startRecording}
          disabled={loading || transcribing}
          aria-label="Record your response"
          className="flex items-center justify-center w-16 h-16 rounded-full bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 disabled:text-slate-500 text-white shadow-lg transition-all hover:scale-105 active:scale-95"
        >
          {(transcribing || loading)
            ? <Loader2 size={24} className="animate-spin" />
            : <Mic size={24} />}
        </button>
      ) : (
        <button
          onClick={stopRecordingAndSend}
          aria-label="Stop recording and send"
          className="flex items-center justify-center w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 text-white shadow-lg transition-all animate-pulse"
        >
          <MicOff size={24} />
        </button>
      )}
      <span className="text-xs text-slate-500 font-mono min-h-[1rem]">
        {statusText}
      </span>
      <button
        onClick={voice.toggleVoiceMode}
        className="text-[11px] text-slate-600 hover:text-slate-400 transition-colors mt-0.5"
      >
        Use text instead
      </button>
    </div>
  );
}
